import React, { useState } from 'react';
import { AbsorbedPhase, UnscheduledPhase, UnscheduledStep } from './progressData';

interface UnscheduledBannerProps {
    // Only the undated tasks/subtasks the chart drew no row for. An undated item that still earns a
    // row — an event-only row, or a header nesting dated subtasks — is on screen, so it is not
    // reported here and the counts match what the reader can see.
    phases: UnscheduledPhase[];
    steps: UnscheduledStep[];
    // Collapsed tasks with no row of their own, because their events are on the task that absorbed them.
    absorbed: AbsorbedPhase[];
}

const UnscheduledBanner: React.FC<UnscheduledBannerProps> = ({ phases, steps, absorbed }) => {
    const [expanded, setExpanded] = useState(false);

    if (!phases.length && !steps.length && !absorbed.length) return null;

    const phasesByModule = new Map<string, { abbrev: string; name: string; phases: string[] }>();
    phases.forEach(p => {
        const entry = phasesByModule.get(p.moduleKey) ?? { abbrev: p.moduleAbbrev, name: p.moduleName, phases: [] };
        entry.phases.push(p.name);
        phasesByModule.set(p.moduleKey, entry);
    });
    const moduleLabel = (m: { name: string; abbrev: string }) => `${m.name} (${m.abbrev})`;

    const totalPhases = phases.length;
    const totalSteps = steps.length;
    const parts: string[] = [];
    if (totalPhases) parts.push(`${totalPhases} task${totalPhases === 1 ? '' : 's'}`);
    if (totalSteps) parts.push(`${totalSteps} subtask${totalSteps === 1 ? '' : 's'}`);
    const sentences: string[] = [];
    if (parts.length) sentences.push(`${parts.join(' and ')} not shown — missing schedule dates.`);
    if (absorbed.length) sentences.push(`${absorbed.length} task${absorbed.length === 1 ? '' : 's'} absorbed into another task.`);
    const summary = sentences.join(' ');

    return (
        <div className="pt-unscheduled-banner" style={{
            background: '#FFF8E1',
            border: '1px solid #FFE082',
            borderRadius: 4,
            padding: '8px 12px',
            margin: '4px 0 8px 0',
            fontSize: 13,
            color: '#5D4037',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1 }}>{summary}</span>
                <button
                    type="button"
                    onClick={() => setExpanded(e => !e)}
                    style={{
                        background: 'transparent',
                        border: '1px solid #C9A227',
                        borderRadius: 3,
                        color: '#5D4037',
                        cursor: 'pointer',
                        fontSize: 12,
                        padding: '2px 8px',
                    }}
                >
                    {expanded ? 'Hide details' : 'Show details'}
                </button>
            </div>
            {expanded && (
                <div style={{ marginTop: 8, paddingLeft: 4 }}>
                    {Array.from(phasesByModule.values()).map(m => (
                        <div key={m.abbrev} style={{ marginBottom: 4 }}>
                            <strong>{moduleLabel(m)}</strong> - {m.phases.join(', ')}
                        </div>
                    ))}
                    {!!totalSteps && (
                        <div style={{ marginTop: 6 }}>
                            <strong>Subtasks:</strong>{' '}
                            {steps.slice(0, 20).map(s => `${s.moduleAbbrev} · ${s.phaseName} › ${s.name}`).join('; ')}
                            {steps.length > 20 ? ` … and ${steps.length - 20} more` : ''}
                        </div>
                    )}
                    {absorbed.map(a => (
                        <div key={a.name} style={{ marginTop: 6 }}>
                            <strong>{a.name}</strong> - absorbed into {a.absorbedInto}; its events are shown there.
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UnscheduledBanner;
